import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {produce} from 'immer'
import { ACCOUNTING_FILES_MAX_UPLOADS } from '../../../constants'
import { ISydDocument } from 'models/shared.models'
import { UploadedFile } from 'models/general'
import {
  AccountingForm,
  IAccountingFileType,
  IAccountingFileStatus,
  IAccountingStepFiveUploadColumn,
  IAccountingStepContinueButtonProps,
  TAccountingFileTypes,
} from 'models/accounting.models'
import { ACCOUNTING_PROCESS_STEPS } from 'lib/enums/accounting.enums'
import {
  accountingStepFiveFirstFileCategoryType,
  accountingStepFiveFourthFileCategoryType,
  accountingStepFiveSecondFileCategoryType,
  accountingStepFiveThirdFileCategoryType,
  accountingStepFourAccountingDocumentation,
  getFilesToValidate,
  getInvalidFiles,
  getTotalFilesPerType,
  getUploadedAndValidatedFiles,
  getUploadedFiles,
  getUploadingFile,
  getValidFiles,
} from 'lib/utils/Accounting/accounting.utils'
import { errorToastOptions } from 'lib/utils/toast.utils'
import { useAccountingContext } from 'containers/Accounting/AccountingContainer/AccountingContainer'
import useAccountingStepFiveUploadColumns from './useAccountingStepFiveUploadColumns'
import useAccountingStepFiveValidationSteps from './useAccountingStepFiveValidationSteps'

export interface IAccountingStepFiveState {
  additionalDocuments: boolean
  clientCommentsDialog: boolean
  firstCategoryFileStatuses: IAccountingFileStatus[]
  limitFileView: boolean
  selectedFile?: ISydDocument | UploadedFile
  thirdCategoryFileStatuses: IAccountingFileStatus[]
  validCommentsDialog: boolean
  validRenameDialog: boolean
}

export interface IUseAccountingStepFive extends IAccountingStepFiveState {
  continueButtonProps: IAccountingStepContinueButtonProps
  dynamicFileUploadColumns: IAccountingStepFiveUploadColumn<IAccountingStepFiveState>[]
  firstCategoryFilesToValidate: ISydDocument[]
  // listForFileNameValidation: (string | undefined)[]
  proxyFinancialStatesFiles: UploadedFile[]
  totalFirstCategoryFiles: ISydDocument[]
  uploadedAndValidatedFirstCategoryFiles: ISydDocument[]
  uploadedFinancialStatesFiles: ISydDocument[]
  uploadedFirstCategoryFiles: ISydDocument[]
  uploadedFourthCategoryFiles: ISydDocument[]
  uploadedPreviousStepsRequiredFiles: ISydDocument[]
  withFinancialStates: boolean
  fileUploadHandler: (
    totalUploadItems: ISydDocument[],
    additionalValidFiles?: ISydDocument[],
    additionalInvalidFiles?: ISydDocument[],
    withStepSubmit?: boolean
  ) => (files: UploadedFile[]) => void
  handleCommentsFormSubmit: (form: any) => Promise<any>
  handleRenameFormSubmit: (form: any) => Promise<any>
  updateStepFiveState: (
    update: (draft: IAccountingStepFiveState) => void
  ) => void
  accountingStepFourAccountingDocumentation?: IAccountingFileType
}

const useAccountingStepFive = (): IUseAccountingStepFive => {
  const {
    filesInProcess,
    filesToUpload,
    fileUploadHandlerFactory,
    handleFileRename,
    handleStepSubmit,
  } = useAccountingContext()

  const totalFirstCategoryFiles = useMemo(
    () =>
      getTotalFilesPerType(
        filesInProcess,
        accountingStepFiveFirstFileCategoryType
      ),
    [filesInProcess]
  )

  const totalSecondCategoryFiles = getTotalFilesPerType(
    filesInProcess,
    accountingStepFiveSecondFileCategoryType
  )

  const totalThirdCategoryFiles = useMemo(
    () =>
      getTotalFilesPerType(
        filesInProcess,
        accountingStepFiveThirdFileCategoryType
      ),
    [filesInProcess]
  )

  const totalFourthCategoryFiles = getTotalFilesPerType(
    filesInProcess,
    accountingStepFiveFourthFileCategoryType
  )

  const [accountingStepFiveState, setAccountingStepFiveState] =
    useState<IAccountingStepFiveState>({
      additionalDocuments: totalFourthCategoryFiles.length > 0,
      clientCommentsDialog: false,
      firstCategoryFileStatuses: totalFirstCategoryFiles.map(f => ({
        id: f.idWsfile,
        status: f.statusText,
      })),
      limitFileView: true,
      thirdCategoryFileStatuses: totalThirdCategoryFiles.map(f => ({
        id: f.idWsfile,
        status: f.statusText,
      })),
      validCommentsDialog: false,
      validRenameDialog: false,
    })

  const {
    additionalDocuments,
    firstCategoryFileStatuses,
    selectedFile,
    thirdCategoryFileStatuses,
  } = accountingStepFiveState

  const updateStepFiveState = useCallback(
    (update: (draft: IAccountingStepFiveState) => void) => {
      setAccountingStepFiveState(currentState => produce(currentState, update))
    },
    []
  )

  const uploadingFirstFile = getUploadingFile(
    filesToUpload,
    accountingStepFiveFirstFileCategoryType
  )
  const uploadingSecondFile = getUploadingFile(
    filesToUpload,
    accountingStepFiveSecondFileCategoryType
  )
  const uploadingThirdFile = getUploadingFile(
    filesToUpload,
    accountingStepFiveThirdFileCategoryType
  )
  const uploadingFourthFile = getUploadingFile(
    filesToUpload,
    accountingStepFiveFourthFileCategoryType
  )

  const proxyFinancialStatesFiles = filesToUpload.filter(
    f => accountingStepFourAccountingDocumentation?.type === f?.description
  )

  const uploadedFirstCategoryFiles = getUploadedFiles(
    filesInProcess,
    accountingStepFiveFirstFileCategoryType
  )

  const uploadedSecondCategoryFiles = getUploadedFiles(
    filesInProcess,
    accountingStepFiveSecondFileCategoryType
  )

  const uploadedThirdCategoryFiles = getUploadedFiles(
    filesInProcess,
    accountingStepFiveThirdFileCategoryType
  )

  const uploadedFourthCategoryFiles = getUploadedFiles(
    filesInProcess,
    accountingStepFiveFourthFileCategoryType
  )

  const uploadedPreviousStepsRequiredFiles = filesInProcess.filter(
    f =>
      ![
        accountingStepFourAccountingDocumentation?.type,
        accountingStepFiveFirstFileCategoryType?.type,
        accountingStepFiveSecondFileCategoryType?.type,
        accountingStepFiveThirdFileCategoryType?.type,
        accountingStepFiveFourthFileCategoryType?.type,
      ].includes(f?.description as TAccountingFileTypes)
  )

  const uploadedFinancialStatesFiles = filesInProcess.filter(
    f => accountingStepFourAccountingDocumentation?.type === f?.description
  )

  const withFinancialStates = uploadedFinancialStatesFiles.reduce(
    (acc, f) => acc && f.validated,
    uploadedFinancialStatesFiles.length > 0
  )

  const firstCategoryFilesToValidate = getFilesToValidate(
    filesInProcess,
    accountingStepFiveFirstFileCategoryType
  )
  const thirdCategoryFilesToValidate = getFilesToValidate(
    filesInProcess,
    accountingStepFiveThirdFileCategoryType
  )

  const firstCategoryValidFiles = getValidFiles(
    totalFirstCategoryFiles,
    firstCategoryFileStatuses
  )

  const thirdCategoryValidFiles = getValidFiles(
    totalThirdCategoryFiles,
    thirdCategoryFileStatuses
  )

  const firstCategoryInvalidFiles = getInvalidFiles(
    totalFirstCategoryFiles,
    firstCategoryFileStatuses
  )

  const thirdCategoryInvalidFiles = getInvalidFiles(
    totalThirdCategoryFiles,
    thirdCategoryFileStatuses
  )

  const uploadedAndValidatedFirstCategoryFiles = getUploadedAndValidatedFiles(
    filesInProcess,
    accountingStepFiveFirstFileCategoryType
  )

  const uploadedAndValidatedThirdCategoryFiles = getUploadedAndValidatedFiles(
    filesInProcess,
    accountingStepFiveThirdFileCategoryType
  )

  const { dynamicFileUploadColumns } = useAccountingStepFiveUploadColumns({
    totalFirstCategoryFiles,
    totalSecondCategoryFiles,
    totalThirdCategoryFiles,
    totalFourthCategoryFiles,
    uploadedSecondCategoryFiles,
    uploadedThirdCategoryFiles,
    firstCategoryFilesToValidate,
    thirdCategoryFilesToValidate,
    firstCategoryValidFiles,
    firstCategoryInvalidFiles,
    thirdCategoryValidFiles,
    thirdCategoryInvalidFiles,
    uploadedAndValidatedFirstCategoryFiles,
    uploadedAndValidatedThirdCategoryFiles,
    firstCategoryFileStatuses,
    uploadedFirstCategoryFiles,
    thirdCategoryFileStatuses,
    uploadedFourthCategoryFiles,
    accountingStepFiveFirstFileCategoryType,
    accountingStepFiveSecondFileCategoryType,
    accountingStepFiveThirdFileCategoryType,
    accountingStepFiveFourthFileCategoryType,
    uploadingFirstFile,
    uploadingSecondFile,
    uploadingThirdFile,
    uploadingFourthFile,
    updateState: updateStepFiveState,
  })

  const { continueButtonProps } = useAccountingStepFiveValidationSteps({
    totalThirdCategoryFiles,
    additionalDocuments,
    firstCategoryFileStatuses,
    thirdCategoryFileStatuses,
    uploadedFirstCategoryFiles,
    uploadedThirdCategoryFiles,
    uploadedFourthCategoryFiles,
    firstCategoryFilesToValidate,
    thirdCategoryFilesToValidate,
    firstCategoryValidFiles,
    thirdCategoryValidFiles,
    firstCategoryInvalidFiles,
    thirdCategoryInvalidFiles,
    uploadedAndValidatedFirstCategoryFiles,
    uploadedAndValidatedThirdCategoryFiles,
  })

  const fileUploadHandler = fileUploadHandlerFactory(
    ACCOUNTING_PROCESS_STEPS.IMPUESTOS_PAGADOS,
    ACCOUNTING_FILES_MAX_UPLOADS
  )

  const handleCommentsFormSubmit = async (form: AccountingForm) => {
    handleStepSubmit(
      ACCOUNTING_PROCESS_STEPS.IMPUESTOS_PAGADOS,
      form.comment,
      [],
      firstCategoryInvalidFiles
    )
    updateStepFiveState(draft => {
      draft.clientCommentsDialog = false
    })
  }

  const handleRenameFormSubmit = async (form: AccountingForm) => {
    if (filesInProcess.some(f => f.name === form.name)) {
      toast.error(
        'No puedes subir más de un archivo con el mismo nombre durante el proceso',
        errorToastOptions
      )
      return
    }

    selectedFile && handleFileRename(form.name, selectedFile)
    updateStepFiveState(draft => {
      draft.selectedFile = undefined
    })
  }

  useEffect(() => {
    updateStepFiveState(draft => {
      draft.firstCategoryFileStatuses = totalFirstCategoryFiles.map(f => ({
        id: f.idWsfile,
        status: f.statusText,
      }))
      draft.thirdCategoryFileStatuses = totalThirdCategoryFiles.map(f => ({
        id: f.idWsfile,
        status: f.statusText,
      }))
    })
  }, [totalFirstCategoryFiles, totalThirdCategoryFiles, updateStepFiveState])

  return {
    ...accountingStepFiveState,
    accountingStepFourAccountingDocumentation,
    continueButtonProps,
    dynamicFileUploadColumns,
    firstCategoryFilesToValidate,
    proxyFinancialStatesFiles,
    totalFirstCategoryFiles,
    uploadedAndValidatedFirstCategoryFiles,
    uploadedFinancialStatesFiles,
    uploadedFirstCategoryFiles,
    uploadedFourthCategoryFiles,
    uploadedPreviousStepsRequiredFiles,
    withFinancialStates,
    fileUploadHandler,
    handleCommentsFormSubmit,
    handleRenameFormSubmit,
    updateStepFiveState,
  }
}

export default useAccountingStepFive
